import { getNetworkStatus } from "@base/model/hooks/useNetworkStatus";
import type { AnyRouter } from "@tanstack/react-router";
import { getProjectGroup } from "./baseLogger";

/**
 * Mixpanel 은 중첩 객체 키로 breakdown/필터를 걸 수 없다.
 * "3G 유저의 업로드 실패율" 같은 세그먼트 질의를 위해 네트워크 정보를 평탄화해서 보낸다.
 * Network Information API 미지원 브라우저(Safari/Firefox)는 `null` 로 남겨
 * "값이 없음"과 "수집 누락"을 구분할 수 있게 한다.
 */
export function getNetworkProperties() {
  const network = getNetworkStatus();

  return {
    network_effective_type: network.effectiveType ?? null,
    network_type: network.type ?? null,
    network_downlink: network.downlink ?? null,
    network_rtt: network.rtt ?? null,
    network_save_data: network.saveData ?? null,
  };
}

/** 이벤트에 실을 라우트 패턴을 읽기 위해 등록해 둔 라우터. */
let analyticsRouter: AnyRouter | null = null;

/** 앱 부팅 시 한 번만 호출한다. */
export function registerAnalyticsRouter(router: AnyRouter): void {
  analyticsRouter = router;
}

/**
 * 현재 화면의 라우트 패턴(`/_authenticated/projects/$projectId/log/$logId/`).
 *
 * `pathname` 은 `/projects/123/log/456` 처럼 식별자를 그대로 담고 있어서, 화면 단위로
 * 집계하려면 값마다 새 항목이 생기고(카디널리티 폭발) breakdown 도 걸리지 않는다.
 * 매칭된 라우트 id 를 따로 실어 보내야 "일일 로그 상세 조회 수" 같은 질의가 가능해진다.
 *
 * 구독으로 캐시하지 않고 **호출 시점에 라우터 상태를 직접 읽는다.** 캐시하면 값을 갱신하는
 * 구독 콜백과 이벤트를 발행하는 컴포넌트 effect 중 무엇이 먼저 도는지에 값의 정확성이
 * 달리게 되는데, 그 순서는 라우터의 transition 처리에 따라 달라져 보장할 수 없다.
 * 라우터가 이미 새 매치를 들고 있어야 그 화면이 렌더되므로, 이벤트 시점에 읽으면
 * 순서와 무관하게 항상 현재 화면이 나온다.
 *
 * 슈퍼 프로퍼티(`mixpanel.register`)로 심지 않는 이유도 같다. 슈퍼 프로퍼티는 localStorage 에
 * 영속되므로, 다음 실행에서 라우터가 뜨기 전에 발생하는 이벤트(web-vitals, 오프라인 구간
 * 지연 보고 등)에 직전 세션의 화면이 붙는다.
 */
export function getRoutePattern(): string | null {
  try {
    // 마지막 매치가 실제로 렌더된 리프 라우트다. 상위 레이아웃 라우트는 화면을 식별하지 못한다.
    const matches = analyticsRouter?.state.matches;
    return matches?.[matches.length - 1]?.routeId ?? null;
  } catch {
    // 계측 실패로 이벤트 전송이 막히면 안 된다.
    return null;
  }
}

/**
 * 이벤트가 귀속돼야 할 화면과 현장.
 *
 * 공통 프로퍼티는 기본적으로 **전송 시점**의 값을 읽는다. 대부분의 이벤트는 발생과 전송이
 * 같은 화면에서 일어나므로 그게 맞지만, 이탈·종료·응답 지연처럼 **관측이 끝난 뒤에
 * 전송되는** 이벤트는 이미 다음 화면으로 넘어간 상태다. 그대로 두면 "작성 화면에서 이탈"이
 * "이동한 목적지에서 이탈"로 기록돼 이탈 분포가 통째로 어긋난다.
 *
 * 현장 group 도 같은 문제를 갖는다. group 은 슈퍼 프로퍼티라 전송 시점 기준으로 붙으므로,
 * 현장 7 에서 띄운 요청이 현장 8 로 이동한 뒤 실패하면 그 실패가 현장 8 에 기록된다.
 *
 * 그런 이벤트는 관측이 **시작된** 시점에 이 값을 떠 두었다가 전송할 때 넘긴다.
 */
export type EventContext = {
  pathname: string;
  route_pattern: string | null;
  /** 관측 시점의 현장 group. 프로젝트 범위 화면이 아니었으면 `null`. */
  project_group: string[] | null;
};

export function captureEventContext(): EventContext {
  return {
    pathname: location.pathname,
    route_pattern: getRoutePattern(),
    project_group: getProjectGroup(),
  };
}
